import { useState, useEffect } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './statementResult.css';
import { formatOdds } from '../../utils/formatOdds'

/**
 * Account Statement row ka "Details" modal — original admin jaisa:
 *   title "Details" + blue round close
 *   breadcrumb (Sport -> League -> Match -> Market / casino -> game/R.No.)  |  Game Time (right)
 *   Winner: X
 *   ( ) All ( ) Back ( ) Lay ( ) Deleted          Total Bets: N   Total Win: X
 *   Username | Nation | Rate | Bhav | Amount | Win | Date | IP | B Details | Action
 *
 *   meta  = { path, winner, gameTime }
 *   bets  = normalized rows: { id, username, nation, rate, bhav, amount, win, date, ip, browser, side, deleted }
 *           (sports → normalizeSportsBet, casino → mapCasinoBet)
 *   error = message shown inside the table when the fetch failed
 *   children = optional result content above the filters (casino cards)
 */

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'back', label: 'Back' },
  { id: 'lay', label: 'Lay' },
  { id: 'delete', label: 'Deleted' },
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function fmt2(v) {
  if (v === null || v === undefined || v === '' || v === '-') return '';
  const n = Number(String(v).replace(/,/g, ''));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const isBlank = (v) => v == null || String(v).trim() === '' || String(v).trim().toLowerCase() === 'null';
const marketTypeOf = (b) => String(b.market_type ?? b.marketType ?? '').toLowerCase();

const isLayBet = (b) => {
  if (marketTypeOf(b) === 'oddeven') return false;
  if (b.side) return String(b.side).toUpperCase() === 'LAY';
  const t = String(b.betType ?? b.bet_type ?? '').toLowerCase();
  return t === 'lay' || t === 'no';
};
const isBadloRow = (b) => b.badlo === true || String(b.badlo) === 'true';
const isDeletedBet = (b) => {
  if (b.deleted === true) return true;
  const s = `${b.status ?? ''} ${b.result_status ?? b.resultStatus ?? ''}`.toLowerCase();
  return s.includes('deleted') || s.includes('void') || s.includes('refund') || s.includes('cancel');
};
const NO_BHAV_MARKETS = new Set(['oddeven', 'fancy1']);
const isKhado = (b) => marketTypeOf(b) === 'khado';
const betBhav = (b) => {
  if (b.casino) return '-';
  if (isKhado(b)) return isBlank(b.fancy_name) ? '-' : String(b.fancy_name);
  if (NO_BHAV_MARKETS.has(marketTypeOf(b))) return '';
  const raw = [b.bhav, b.size].find((x) => x !== undefined && x !== null && x !== '');
  if (raw === undefined || num(raw) === 0) return '-';
  return String(raw);
};
const betNation = (b) => {
  if (isKhado(b) && !isBlank(b.selection_name)) {
    return isBlank(b.fancy_name) ? b.selection_name : `${b.selection_name}-${b.fancy_name}`;
  }
  return [b.nation, b.fancy_name, b.selection_name].find((v) => !isBlank(v)) ?? '';
};

function pad(n) { return String(n).padStart(2, '0'); }
export function fmtDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Raw SportsBet row (bet-result endpoint / buildSuperOverStatement) → table row.
export function normalizeSportsBet(b) {
  const badlo = isBadloRow(b);
  const lay = isLayBet(b);
  const amountRaw = b.amount ?? b.stake_amount ?? b.stake;
  return {
    id: b.id,
    username: b.user?.username ?? b.username ?? '',
    nation: betNation(b),
    rate: b.rate ?? b.odds ?? '',
    bhav: betBhav(b),
    amount: badlo && lay ? -Math.abs(num(amountRaw)) : amountRaw,
    win: b.win == null ? null : num(b.win),
    date: b.date ?? b.created_at,
    ip: b.ipAddress ?? b.ip_address ?? '',
    browser: b.browser ?? b.browser_detail ?? '',
    side: badlo ? null : lay ? 'lay' : 'back',
    deleted: isDeletedBet(b),
  };
}

export default function StatementDetailsModal({ show, onHide, meta = {}, bets = [], error = null, children }) {
  const [filter, setFilter] = useState('all');
  const [picked, setPicked] = useState(() => new Set());
  const rows = Array.isArray(bets) ? bets : [];

  useEffect(() => { if (show) setFilter('all'); }, [show]);
  useEffect(() => { setPicked(new Set()); }, [show, filter, rows.length]);

  // Deleted bets only under their own tab — All/Back/Lay exclude them.
  const visible = rows.filter((b) => {
    if (filter === 'delete') return b.deleted;
    if (b.deleted) return false;
    if (filter === 'all') return true;
    return String(b.side || '').toLowerCase() === filter;
  });

  const counted = picked.size ? visible.filter((_, i) => picked.has(i)) : visible;
  const totalWin = counted.reduce((sum, b) => sum + num(b.win), 0);
  const togglePick = (i) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="stmt-details-dialog">
      <Modal.Header>
        <Modal.Title>Details</Modal.Title>
        <button type="button" className="stmt-details-close" onClick={onHide} aria-label="Close">×</button>
      </Modal.Header>
      <Modal.Body>
        {children ? <div className="stmt-details-result">{children}</div> : null}
        <div className="stmt-details-meta">
          <div>
            <div>{meta.path}</div>
            <div>Winner: {meta.winner}</div>
          </div>
          <div className="stmt-details-time">
            <div>Game Time:</div>
            <div>{meta.gameTime}</div>
          </div>
        </div>

        <div className="stmt-details-filters">
          <div>
            {FILTERS.map((f) => (
              <div className="form-check form-check-inline" key={f.id}>
                <input
                  type="radio"
                  className="form-check-input"
                  id={`stmt-details-filter-${f.id}`}
                  name="stmt-details-filter"
                  checked={filter === f.id}
                  onChange={() => setFilter(f.id)}
                />
                <label className="form-check-label" htmlFor={`stmt-details-filter-${f.id}`}>{f.label}</label>
              </div>
            ))}
          </div>
          <div className="stmt-details-totals">
            Total Bets: <span className="count">{counted.length}</span>
            &nbsp;&nbsp; Total Win:{' '}
            <span className={totalWin < 0 ? 'text-danger' : 'text-success'}>{totalWin.toFixed(2)}</span>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table stmt-details-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Nation</th>
                <th>Rate</th>
                <th>Bhav</th>
                <th>Amount</th>
                <th>Win</th>
                <th>Date</th>
                <th>IP</th>
                <th>B Details</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr><td colSpan="10" className="text-center text-muted">{error}</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan="10" className="text-center text-muted">No records found</td></tr>
              ) : visible.map((b, i) => {
                const win = num(b.win);
                return (
                  <tr className={b.side || undefined} key={b.id ?? i}>
                    <td>{b.username}</td>
                    <td>{b.nation}</td>
                    <td>{formatOdds(b.rate, '')}</td>
                    <td>{b.bhav}</td>
                    <td>{fmt2(b.amount)}</td>
                    <td>
                      <span className={win < 0 ? 'text-danger' : 'text-success'}>{b.win == null ? '' : fmt2(win)}</span>
                    </td>
                    <td>{fmtDateTime(b.date)}</td>
                    <td>{b.ip}</td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        container={document.body}
                        overlay={<Tooltip id={`stmt-details-browser-${i}`}>{b.browser || 'Not recorded for this bet'}</Tooltip>}
                      >
                        <span className="stmt-detail-link">Detail</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <input type="checkbox" className="stmt-action-check" title="Toggle Row Selected" checked={picked.has(i)} onChange={() => togglePick(i)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
}
